<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<!-- SPEC 41 / P37-18 — Meus Negócios e processos administrativos -->
<script setup>
/* eslint-disable no-alert */
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';

import LaisSettingsAPI from 'dashboard/api/laisSettings';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisTabs from 'dashboard/components-next/LaisTabs.vue';
import BusinessIdentificationBlock from './meusNegocios/BusinessIdentificationBlock.vue';
import BusinessOperationBlock from './meusNegocios/BusinessOperationBlock.vue';
import BusinessPoliciesBlock from './meusNegocios/BusinessPoliciesBlock.vue';
import BusinessSectorsBlock from './meusNegocios/BusinessSectorsBlock.vue';
import BusinessTestsBlock from './meusNegocios/BusinessTestsBlock.vue';

const TEST_PROMPTS = [
  'Qual o horário de atendimento?',
  'Preciso falar com o financeiro',
  'Quais documentos para alugar?',
  'Posso visitar com chave na portaria?',
];

const BUSINESS_TABS = [
  {
    id: 'identificacao',
    label: 'Identificação',
    icon: 'i-lucide-badge-info',
  },
  {
    id: 'setores',
    label: 'Setores',
    icon: 'i-lucide-building-2',
  },
  {
    id: 'politicas',
    label: 'Políticas',
    icon: 'i-lucide-shield-check',
  },
  {
    id: 'locacao',
    label: 'Locação',
    icon: 'i-lucide-key-round',
  },
  {
    id: 'venda',
    label: 'Venda',
    icon: 'i-lucide-handshake',
  },
  {
    id: 'testes',
    label: 'Testes',
    icon: 'i-lucide-message-circle',
  },
];

const DEFAULT_TAB = BUSINESS_TABS[0].id;
const BUSINESS_TAB_IDS = BUSINESS_TABS.map(tab => tab.id);
const BUSINESS_TAB_ID_SET = new Set(BUSINESS_TAB_IDS);
const BUSINESS_TAB_LABELS = BUSINESS_TABS.reduce(
  (labels, tab) => ({ ...labels, [tab.id]: tab.label }),
  {}
);
const UNSAVED_TAB_CONFIRM_MESSAGE =
  'Você tem alterações não salvas nesta aba. Sair sem salvar?';

const defaultSectorContact = () => ({
  sector: 'comercial',
  label: '',
  whatsapp_phone: '',
  description: '',
  active: true,
  site_profile_enabled: false,
  site_username: '',
  site_bio: '',
  site_logo: '',
  responsible_user_id: null,
});

const defaultBusinessProfile = () => ({
  identification: {
    name: '',
    address: '',
    site: '',
    region: '',
    business_hours: '',
    logo: '',
  },
  sector_contacts: [],
  policies: {
    contract_signature: 'ambos',
    visit_key_rule: 'imobiliaria',
    visit_required_document: '',
    notes: '',
  },
  rent: {
    enabled: false,
    business_hours: '',
    required_documents: '',
    guarantees: '',
    analysis_deadline: '',
    penalties: '',
    agent_naming: '',
  },
  sale: {
    enabled: false,
    business_hours: '',
    required_documents: '',
    financing: '',
    analysis_deadline: '',
    penalties: '',
    agent_naming: '',
  },
});

const route = useRoute();
const router = useRouter();
const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');
const agents = useMapGetter('agents/getAgents');

const isLoading = ref(false);
const warnings = ref([]);
const selectedTestPrompt = ref(TEST_PROMPTS[0]);
const activeTab = ref(DEFAULT_TAB);
const tabSnapshots = reactive({});
const tabSaveState = reactive(
  BUSINESS_TAB_IDS.reduce((states, tab) => {
    states[tab] = { saving: false, error: '' };
    return states;
  }, {})
);
const form = reactive(defaultBusinessProfile());

const clone = value => JSON.parse(JSON.stringify(value));
const firstQueryValue = value => (Array.isArray(value) ? value[0] : value);

const normalizeBusinessTab = value => {
  const tab = String(firstQueryValue(value) || '').toLowerCase();
  return BUSINESS_TAB_ID_SET.has(tab) ? tab : DEFAULT_TAB;
};

const normalizeObject = (source, defaults) => ({
  ...defaults,
  ...(source || {}),
});

const normalizeBusinessProfile = profile => {
  const defaults = defaultBusinessProfile();

  return {
    identification: normalizeObject(
      profile?.identification,
      defaults.identification
    ),
    sector_contacts: (profile?.sector_contacts || []).map(contact => ({
      ...defaultSectorContact(),
      ...contact,
    })),
    policies: normalizeObject(profile?.policies, defaults.policies),
    rent: normalizeObject(profile?.rent, defaults.rent),
    sale: normalizeObject(profile?.sale, defaults.sale),
  };
};

const replaceObject = (target, source) => {
  Object.keys(target).forEach(key => delete target[key]);
  Object.assign(target, clone(source));
};

const applyProfile = profile => {
  const normalizedProfile = normalizeBusinessProfile(profile || {});

  replaceObject(form.identification, normalizedProfile.identification);
  form.sector_contacts.splice(
    0,
    form.sector_contacts.length,
    ...normalizedProfile.sector_contacts
  );
  replaceObject(form.policies, normalizedProfile.policies);
  replaceObject(form.rent, normalizedProfile.rent);
  replaceObject(form.sale, normalizedProfile.sale);
};

const tabValue = tab => {
  switch (normalizeBusinessTab(tab)) {
    case 'identificacao':
      return form.identification;
    case 'setores':
      return form.sector_contacts;
    case 'politicas':
      return form.policies;
    case 'locacao':
      return form.rent;
    case 'venda':
      return form.sale;
    default:
      return {};
  }
};

const tabSignature = tab => JSON.stringify(clone(tabValue(tab)));

const commitTabSnapshot = tab => {
  tabSnapshots[tab] = tabSignature(tab);
};

const commitAllTabSnapshots = () => {
  BUSINESS_TAB_IDS.forEach(commitTabSnapshot);
};

const restoreTabFromSnapshot = tab => {
  const snapshot = tabSnapshots[tab];
  if (!snapshot) return;

  const value = JSON.parse(snapshot);

  switch (tab) {
    case 'identificacao':
      replaceObject(form.identification, value);
      break;
    case 'setores':
      form.sector_contacts.splice(0, form.sector_contacts.length, ...value);
      break;
    case 'politicas':
      replaceObject(form.policies, value);
      break;
    case 'locacao':
      replaceObject(form.rent, value);
      break;
    case 'venda':
      replaceObject(form.sale, value);
      break;
    default:
      break;
  }
};

const hasUnsavedChanges = tab =>
  Boolean(tabSnapshots[tab]) && tabSnapshots[tab] !== tabSignature(tab);

const activeTabDefinition = computed(
  () =>
    BUSINESS_TABS.find(tab => tab.id === activeTab.value) || BUSINESS_TABS[0]
);
const activeTabState = computed(() => tabSaveState[activeTab.value] || {});
const isSaveableTab = computed(() => activeTab.value !== 'testes');
const activeTabHasUnsavedChanges = computed(
  () => isSaveableTab.value && hasUnsavedChanges(activeTab.value)
);

const businessProfilePayload = () =>
  clone({
    identification: form.identification,
    sector_contacts: form.sector_contacts,
    policies: form.policies,
    rent: form.rent,
    sale: form.sale,
  });

const syncRouteTab = (tab, mode = 'push') => {
  const normalizedTab = normalizeBusinessTab(tab);
  if (firstQueryValue(route.query.tab) === normalizedTab) return;

  router[mode]({
    name: 'meus_negocios_index',
    params: route.params,
    query: { ...route.query, tab: normalizedTab },
  }).catch(() => {});
};

const confirmUnsavedTabChange = () => {
  if (typeof window === 'undefined') return true;
  return window.confirm(UNSAVED_TAB_CONFIRM_MESSAGE);
};

const canLeaveActiveTab = () => {
  if (activeTabState.value.saving) {
    useAlert('Aguarde o salvamento desta aba terminar.');
    return false;
  }

  if (!activeTabHasUnsavedChanges.value) return true;
  if (!confirmUnsavedTabChange()) return false;

  restoreTabFromSnapshot(activeTab.value);
  tabSaveState[activeTab.value].error = '';
  return true;
};

const selectBusinessTab = tab => {
  const normalizedTab = normalizeBusinessTab(tab);
  if (normalizedTab === activeTab.value) {
    syncRouteTab(normalizedTab, 'replace');
    return;
  }

  if (!canLeaveActiveTab()) return;

  activeTab.value = normalizedTab;
  syncRouteTab(normalizedTab);
};

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await LaisSettingsAPI.getBusinessProfile();
    applyProfile(data.business_profile || {});
    warnings.value = data.warnings || [];
    commitAllTabSnapshots();
    BUSINESS_TAB_IDS.forEach(tab => {
      tabSaveState[tab].error = '';
    });
  } catch {
    useAlert('Não foi possível carregar Meus Negócios.');
  } finally {
    isLoading.value = false;
  }
};

const addSector = () => {
  form.sector_contacts.push(defaultSectorContact());
};

const uploadImage = async file => {
  const { fileUrl } = await uploadFile(file, accountId.value);
  return fileUrl;
};

const onIdentificationLogoChange = async event => {
  const file = event.target?.files?.[0];
  if (!file) return;
  try {
    form.identification.logo = await uploadImage(file);
  } catch {
    useAlert('Não foi possível enviar a imagem.');
  } finally {
    event.target.value = '';
  }
};

const onSectorLogoChange = async (event, contact) => {
  const file = event.target?.files?.[0];
  if (!file) return;
  try {
    contact.site_logo = await uploadImage(file);
  } catch {
    useAlert('Não foi possível enviar a imagem.');
  } finally {
    event.target.value = '';
  }
};

const removeSector = index => {
  form.sector_contacts.splice(index, 1);
};

const saveTab = async tab => {
  const normalizedTab = normalizeBusinessTab(tab);
  if (normalizedTab === 'testes') return;

  tabSaveState[normalizedTab].saving = true;
  tabSaveState[normalizedTab].error = '';

  try {
    const { data } = await LaisSettingsAPI.updateBusinessProfile(
      businessProfilePayload()
    );
    applyProfile(data.business_profile || {});
    warnings.value = data.warnings || [];
    commitAllTabSnapshots();
    useAlert(`${BUSINESS_TAB_LABELS[normalizedTab]} salvo.`);
  } catch (error) {
    tabSaveState[normalizedTab].error =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      'Não foi possível salvar esta aba.';
    useAlert(tabSaveState[normalizedTab].error);
  } finally {
    tabSaveState[normalizedTab].saving = false;
  }
};

const saveActiveTab = () => saveTab(activeTab.value);

const testPromptRoute = prompt => ({
  name: 'captain_assistants_index',
  params: {
    accountId: route.params.accountId,
    navigationPath: 'captain_assistants_playground_index',
  },
  query: { prompt },
});

const testPrompt = prompt => {
  selectedTestPrompt.value = prompt;
  router.push(testPromptRoute(prompt));
};

watch(
  () => route.query.tab,
  value => {
    const normalizedTab = normalizeBusinessTab(value);

    if (firstQueryValue(value) !== normalizedTab) {
      syncRouteTab(normalizedTab, 'replace');
      return;
    }

    if (normalizedTab === activeTab.value) return;

    if (!canLeaveActiveTab()) {
      syncRouteTab(activeTab.value, 'replace');
      return;
    }

    activeTab.value = normalizedTab;
  },
  { immediate: true }
);

onBeforeRouteLeave((_to, _from, next) => {
  if (canLeaveActiveTab()) next();
  else next(false);
});

onMounted(() => {
  store.dispatch('agents/get');
  load();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      title="Meus Negócios"
      helper="Processos administrativos que a Lais usa para responder, orientar e encaminhar setores."
    >
      <template #actions>
        <LaisPrimaryButton
          v-if="isSaveableTab"
          icon="i-lucide-save"
          :is-loading="activeTabState.saving"
          :disabled="isLoading"
          @click="saveActiveTab"
        >
          Salvar aba
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div class="mx-auto grid w-full max-w-5xl gap-4">
        <div
          v-if="warnings.length"
          class="rounded-lais border border-[#f0c36d] bg-[#fff8e6] px-4 py-3 text-[13px] text-[#92670c]"
        >
          <p v-for="(warning, index) in warnings" :key="index">
            {{ warning.message }}
          </p>
        </div>

        <div class="md:hidden">
          <label class="sr-only" for="meus-negocios-tab-select">
            Bloco de Meus Negócios
          </label>
          <select
            id="meus-negocios-tab-select"
            data-test="meus-negocios-tab-select"
            :value="activeTab"
            class="w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] font-semibold text-lais-text"
            @change="selectBusinessTab($event.target.value)"
          >
            <option v-for="tab in BUSINESS_TABS" :key="tab.id" :value="tab.id">
              {{ tab.label }}
            </option>
          </select>
        </div>

        <LaisTabs
          class="[&>[role=tablist]]:hidden md:[&>[role=tablist]]:flex"
          :model-value="activeTab"
          :tabs="BUSINESS_TABS"
          aria-label="Blocos de Meus Negócios"
          @update:model-value="selectBusinessTab"
        >
          <template #identificacao>
            <BusinessIdentificationBlock
              v-model="form.identification"
              @logo-change="onIdentificationLogoChange"
            />
          </template>

          <template #setores>
            <BusinessSectorsBlock
              v-model="form.sector_contacts"
              :agents="agents"
              @add-sector="addSector"
              @remove-sector="removeSector"
              @logo-change="onSectorLogoChange"
            />
          </template>

          <template #politicas>
            <BusinessPoliciesBlock v-model="form.policies" />
          </template>

          <template #locacao>
            <BusinessOperationBlock
              v-model="form.rent"
              mode="rent"
              title="Locação"
              enabled-label="Operação de locação habilitada"
              business-hours-label="Horário específico de atendimento"
            />
          </template>

          <template #venda>
            <BusinessOperationBlock
              v-model="form.sale"
              mode="sale"
              title="Venda"
              enabled-label="Operação de venda habilitada"
              business-hours-label="Horário específico"
            />
          </template>

          <template #testes>
            <BusinessTestsBlock
              :prompts="TEST_PROMPTS"
              :selected-prompt="selectedTestPrompt"
              @test="testPrompt"
            />
          </template>
        </LaisTabs>

        <div
          v-if="activeTabState.error"
          data-test="meus-negocios-tab-error"
          class="rounded-lais border border-lais-danger/30 bg-lais-danger/10 px-4 py-3 text-[13px] text-lais-danger"
        >
          {{ activeTabState.error }}
        </div>

        <div
          v-else-if="activeTabHasUnsavedChanges"
          data-test="meus-negocios-tab-dirty"
          class="rounded-lais border border-lais-primary/20 bg-lais-primary-50 px-4 py-3 text-[13px] font-medium text-lais-primary"
        >
          Alterações não salvas em {{ activeTabDefinition.label }}.
        </div>
      </div>
    </div>
  </section>
</template>
